import { useEffect, useRef, useCallback } from 'react'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { OrderNotification } from '../types/websocket'
import { WS_ENDPOINTS } from '../config/api'

interface UseOrderNotificationsProps {
  customerId?: string
  onNotification?: (notification: OrderNotification) => void
  enabled?: boolean
}

export const useOrderNotifications = ({
  customerId,
  onNotification,
  enabled = true
}: UseOrderNotificationsProps) => {
  const clientRef = useRef<Client | null>(null)
  const isConnectedRef = useRef(false)

  const connect = useCallback(() => {
    if (!customerId || !enabled || isConnectedRef.current) {
      return
    }

    // Create WebSocket client
    const client = new Client({
      webSocketFactory: () => new SockJS(WS_ENDPOINTS.sockjs),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        console.log('✅ WebSocket connesso')
        isConnectedRef.current = true

        // Subscribe to customer-specific queue
        client.subscribe(`/queue/orders/${customerId}`, (message) => {
          try {
            const notification: OrderNotification = JSON.parse(message.body)
            console.log('📬 Notifica ricevuta:', notification)

            // Call the callback if provided
            onNotification?.(notification)

            // Also add to global notification container
            if ((window as any).__addOrderNotification) {
              (window as any).__addOrderNotification(notification)
            }
          } catch (error) {
            console.error('❌ Errore parsing notifica:', error)
          }
        })
      },

      onDisconnect: () => {
        console.log('❌ WebSocket disconnesso')
        isConnectedRef.current = false
      },

      onStompError: (frame) => {
        console.error('❌ Errore STOMP:', frame.headers['message'])
        console.error('Dettagli:', frame.body)
      },

      onWebSocketError: (error) => {
        console.error('❌ Errore WebSocket:', error)
      }
    })

    client.activate()
    clientRef.current = client
  }, [customerId, enabled, onNotification])

  const disconnect = useCallback(() => {
    if (clientRef.current && isConnectedRef.current) {
      clientRef.current.deactivate()
      clientRef.current = null
      isConnectedRef.current = false
      console.log('🔌 WebSocket disconnesso manualmente')
    }
  }, [])

  useEffect(() => {
    connect()

    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  return {
    isConnected: isConnectedRef.current,
    disconnect
  }
}
